/**
 * Study content: fetch the bootstrap payload and keep the local IndexedDB cache
 * in sync. The home screen reads chapters from here, not from the network, so
 * the app works offline once content has been fetched at least once.
 */

import { fetchStudyChapter, fetchStudyMain, fetchStudyTopic } from '../auth/api.js';
import { getAppVersion, setAppVersion } from '../auth/storage.js';
import { detectPlatform } from '../platform.js';
import { API_BASE_URL } from '../config.js';
import { STORES, get, getAll, getAllBy, put, remove } from '../db/idb.js';

/* -------------------------------------------------------------------------- */
/* Images (chapter covers & topic thumbnails)                                 */
/* -------------------------------------------------------------------------- */

/** Resolve a possibly-relative image path against the API host. */
function imageUrl(image) {
  const value = typeof image === 'string' ? image.trim() : '';
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `${API_BASE_URL}${value}`;
}

/**
 * Download an image and return it as a Blob so it can live in IndexedDB and be
 * shown offline — a chapter's card cover and a topic's thumbnail both come
 * through here.
 *
 * They are served by our own authenticated image proxy, so the request carries
 * the access token as a Bearer header — same origin/auth as every other API call
 * (mirrors {@link fetchAudioBinaries}). That is also why the stored Blob is the
 * only source the views render from: the path itself is not fetchable by an
 * `<img>`.
 *
 * Best-effort: resolves to `undefined` on a missing URL or any network / HTTP
 * failure, so a broken image never blocks caching the row itself.
 *
 * @param {?string} image Image path (relative to the API host, or absolute).
 * @param {string} jwt Access token, sent as `Authorization: Bearer …`.
 */
async function fetchImageBlob(image, jwt) {
  const url = imageUrl(image);
  if (!url) return undefined;
  try {
    const response = await fetch(url, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!response.ok) return undefined;
    return await response.blob();
  } catch {
    return undefined;
  }
}

/* -------------------------------------------------------------------------- */
/* Chapters                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Fetch `/study/main` and reconcile its chapters into IndexedDB:
 *   - chapter not in the store            → add it
 *   - chapter present, same version       → leave it (keeps any `needUpdate` flag)
 *   - chapter present, version changed    → refresh its fields and flag `needUpdate`
 *                                           (never drop the row — its topics stay cached)
 *   - cached chapter gone from response   → delete it
 *
 * Sends the version stored from the previous response and the runtime platform
 * (so the server knows which version the user is running and whether they're on
 * iOS / Android) and persists the version it returns.
 *
 * Returns the raw payload so callers can use the global app metadata
 * (`name` / `version` / `softUpdate` / `hardUpdate` / `hardLogout` / `header` / `alert` / `info`)
 * in later steps.
 *
 * @param {string} jwt Access token.
 */
export async function refreshFromMain(jwt) {
  const main = await fetchStudyMain(jwt, { version: getAppVersion(), platform: detectPlatform() });
  await syncChapters(Array.isArray(main?.chapters) ? main.chapters : [], jwt);
  if (main && typeof main.version === 'string') setAppVersion(main.version);
  return main;
}

/**
 * Reconcile the remote chapter list into the `chapters` store, caching each
 * chapter's cover as a Blob (`imageBlob`) so the home cards render it offline
 * straight from IndexedDB — the `image` path points at our authenticated image
 * proxy, so it can never be an `<img src>`.
 *
 * @param {Array<object>} remoteChapters `chapters` from `/study/main`.
 * @param {string} jwt Access token — the cover fetches are authenticated.
 */
async function syncChapters(remoteChapters, jwt) {
  const cached = await getAll(STORES.chapters);
  const existingById = new Map(cached.map((chapter) => [chapter.id, chapter]));

  // Upsert: add new chapters, flag the ones whose version changed. Each row is
  // independent, so they go in concurrently — on a cold cache that is one round
  // of cover downloads instead of a catalogue's worth back to back.
  await Promise.all(
    remoteChapters.map(async (chapter) => {
      const existing = existingById.get(chapter.id);

      if (!existing) {
        // new → download the cover once, store the chapter plus fresh local flags
        return put(STORES.chapters, {
          ...chapter,
          needUpdate: false,
          imageBlob: await fetchImageBlob(chapter.image, jwt),
        });
      }

      // Re-download the cover only when its URL moved or we don't have one yet
      // (which is also what back-fills a chapter cached before covers existed).
      const staleImage = existing.image !== chapter.image || !existing.imageBlob;
      const imageBlob = staleImage ? await fetchImageBlob(chapter.image, jwt) : existing.imageBlob;

      if (existing.version !== chapter.version) {
        // Version changed: refresh the chapter's own fields (title, description,
        // version…) but KEEP the row and flag it, so its topics get re-synced the
        // next time it's opened. Nothing local is at stake — the chapter's
        // percentage isn't stored here any more, it is counted when shown.
        return put(STORES.chapters, { ...existing, ...chapter, needUpdate: true, imageBlob });
      }

      // Same version → the cover is the only thing that can still need a write;
      // its path rides along so a moved image isn't re-fetched on every refresh.
      // (Leave any existing `needUpdate` flag alone.)
      if (staleImage) {
        return put(STORES.chapters, { ...existing, image: chapter.image, imageBlob });
      }
      return undefined;
    })
  );

  // Prune: drop cached chapters the server no longer returns.
  const remoteIds = new Set(remoteChapters.map((chapter) => chapter.id));
  await Promise.all(
    cached
      .filter((chapter) => !remoteIds.has(chapter.id))
      .map((chapter) => remove(STORES.chapters, chapter.id))
  );
}

/** Chapters cached in IndexedDB, ordered by their `position`. */
export async function getChapters() {
  const chapters = await getAll(STORES.chapters);
  return chapters.sort((a, b) => a.position - b.position);
}

/** A single cached chapter by id, or `undefined` if it isn't cached. */
export function getChapter(chapterId) {
  return get(STORES.chapters, chapterId);
}

/*
 * Completion lives elsewhere: which topics are finished is the user's data, so it
 * has its own store and its own module (`study/completion.js`), and a chapter's
 * percentage is derived from it on read (`study/progress.js`). Nothing in this
 * file writes or reads it — which is the whole point, since everything here
 * rewrites content rows from the API's answers.
 */

/* -------------------------------------------------------------------------- */
/* Topics (per-chapter "head" rows)                                           */
/* -------------------------------------------------------------------------- */

/**
 * Cached topic rows for one chapter, ordered by their `position`.
 *
 * Read through the `chapterId` index rather than scanning the whole store: these
 * rows carry the cached thumbnails, so a full `getAll` would deserialize every
 * topic's image in the catalogue to answer a question about a single chapter.
 */
export async function getTopics(chapterId) {
  const topics = await getAllBy(STORES.topicsHead, 'chapterId', chapterId);
  return topics.sort((a, b) => a.position - b.position);
}

/**
 * Reconcile a chapter's remote topic list into the `topics_head` store:
 *   - topic not cached          → add it (fresh `needUpdate` flag)
 *   - topic cached, same version → refresh its fields, keep the flag
 *   - topic cached, version changed → refresh + flag `needUpdate`
 *   - cached topic gone from response → delete it
 *
 * `topics_head` holds only the basic per-topic info returned by
 * `/study/chapter/{id}`, tagged with `chapterId`, the local `needUpdate` flag, and
 * the image cached as a Blob (`imageBlob`) so it renders offline straight from
 * IndexedDB. Nothing about completion: a row here can be rewritten from the API's
 * answer without a second thought, because there is no user data on it to lose.
 *
 * @param {string} chapterId Chapter id.
 * @param {Array<object>} remoteTopics Topic list from `/study/chapter/{id}`.
 * @param {string} jwt Access token — the thumbnail fetches are authenticated.
 */
async function syncTopics(chapterId, remoteTopics, jwt) {
  const existingList = await getTopics(chapterId);
  const existingById = new Map(existingList.map((topic) => [topic.id, topic]));

  // Topics are independent of one another, so they reconcile concurrently: on a
  // cold cache this is one round of thumbnail downloads instead of a chapter's
  // worth of them back to back. Same as `fetchAudioBinaries` does for the clips.
  await Promise.all(
    remoteTopics.map(async (topic) => {
      const existing = existingById.get(topic.id);

      if (!existing) {
        // new → download the image once, store the head info plus a fresh flag
        await put(STORES.topicsHead, {
          ...topic,
          chapterId,
          needUpdate: false,
          imageBlob: await fetchImageBlob(topic.image, jwt),
        });
        return;
      }

      // Re-download the thumbnail only when its URL changed or we don't have one yet.
      const imageBlob =
        existing.image === topic.image && existing.imageBlob
          ? existing.imageBlob
          : await fetchImageBlob(topic.image, jwt);

      // existing → refresh the head fields and flag `needUpdate` when the version
      // moved (otherwise keep the current flag). A row cached before v8 may still
      // carry an inert `isDone` here; it rides along unread and unmigrated.
      await put(STORES.topicsHead, {
        ...existing,
        ...topic,
        chapterId,
        needUpdate: existing.version !== topic.version ? true : Boolean(existing.needUpdate),
        imageBlob,
      });
    })
  );

  // Prune: drop cached topics the server no longer returns for this chapter.
  const remoteIds = new Set(remoteTopics.map((topic) => topic.id));
  await Promise.all(
    existingList
      .filter((topic) => !remoteIds.has(topic.id))
      .map((topic) => remove(STORES.topicsHead, topic.id))
  );
}

/**
 * Resolve the topics for a chapter, fetching from the network only when needed:
 *   - `topics_head` empty for this chapter      → fetch and populate
 *   - chapter flagged `needUpdate`              → fetch, re-sync, then clear the flag
 *   - otherwise (cached & fresh)                → return the cache, no request
 *
 * Adding or pruning topics changes what "100% done" means for the chapter, and
 * nothing here has to be told: the percentage is counted from the rows this sync
 * just left behind, every time it is shown (`study/progress.js`).
 *
 * @param {string} jwt Access token.
 * @param {string} chapterId Chapter id.
 * @returns {Promise<{chapter: ?object, topics: Array<object>}>}
 *   `chapter` is the cached chapter row, or the freshly-fetched meta if a fetch
 *   happened (handy for the header when the chapters store wasn't loaded first).
 */
export async function loadTopics(jwt, chapterId) {
  const chapter = await getChapter(chapterId);
  const cached = await getTopics(chapterId);

  // Cached and not flagged → render straight from IndexedDB, no request.
  if (cached.length && !chapter?.needUpdate) {
    return { chapter, topics: cached };
  }

  // Empty cache or stale chapter → fetch the detail payload and reconcile.
  const remote = await fetchStudyChapter(jwt, chapterId);
  await syncTopics(chapterId, Array.isArray(remote?.topics) ? remote.topics : [], jwt);

  // The update ran on this chapter view → clear the chapter's stale flag, and
  // keep the meta we return in step with what we just persisted.
  let meta = chapter;
  if (chapter?.needUpdate) {
    meta = { ...chapter, needUpdate: false };
    await put(STORES.chapters, meta);
  }

  if (!meta) {
    meta = {
      id: remote?.id ?? chapterId,
      title: remote?.title ?? '',
      description: remote?.description ?? null,
      version: remote?.version,
    };
  }

  return { chapter: meta, topics: await getTopics(chapterId) };
}

/* -------------------------------------------------------------------------- */
/* Topic detail (full content + audio)                                        */
/* -------------------------------------------------------------------------- */

/** The cached `topics_head` row for a single topic, or `undefined`. */
export function getTopicHead(topicId) {
  return get(STORES.topicsHead, topicId);
}

/** Resolve a possibly-relative media path against the API host. */
function mediaUrl(src) {
  const value = typeof src === 'string' ? src.trim() : '';
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `${API_BASE_URL}${value}`;
}

/**
 * Download one mp3 and return its raw binary. Stored as binary on purpose —
 * IndexedDB persists ArrayBuffers reliably everywhere (incl. iOS WKWebView,
 * where cached Blobs can misbehave); the readers rehydrate them into `Blob`s for
 * the player ({@link hydrateAudioBlob}, {@link hydratePhraseAudio}).
 *
 * The clips are served by our own authenticated endpoints
 * (`/api/v1/study/audio/{sentenceId}`, `/api/v1/study/audio/phrase/{phraseId}`),
 * so the request carries the access token as a Bearer header — same origin/auth
 * as every other API call.
 *
 * Best-effort: a missing url or any network / HTTP failure resolves to
 * `undefined`, so one broken clip never blocks caching the rest (the player just
 * skips ids with no audio).
 *
 * @param {?string} src Clip path (relative to the API host, or absolute).
 * @param {string} jwt Access token, sent as `Authorization: Bearer …`.
 */
async function fetchAudioBinary(src, jwt) {
  const url = mediaUrl(src);
  if (!url) return undefined;
  try {
    const response = await fetch(url, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!response.ok) return undefined;
    return await response.arrayBuffer();
  } catch {
    return undefined;
  }
}

/**
 * Download a whole `audio` list (`sentenceId → url`) and return an `audio_blob`
 * map (`sentenceId → ArrayBuffer`) keyed by the same ids, skipping the clips
 * {@link fetchAudioBinary} couldn't get.
 *
 * @param {Record<string, string>} audio Map of `sentenceId → url`.
 * @param {string} jwt Access token.
 */
async function fetchAudioBinaries(audio, jwt) {
  const entries = Object.entries(audio && typeof audio === 'object' ? audio : {});
  const binaries = {};
  await Promise.all(
    entries.map(async ([id, url]) => {
      const binary = await fetchAudioBinary(url, jwt);
      if (binary) binaries[id] = binary;
    })
  );
  return binaries;
}

/** Rebuild the stored binary `audio_blob` map into playable `Blob`s (keyed by id). */
function hydrateAudioBlob(row) {
  const source = row?.audio_blob;
  if (!source || typeof source !== 'object') return row;
  const audio_blob = {};
  for (const [id, value] of Object.entries(source)) {
    if (value instanceof Blob)
      audio_blob[id] = value; // already a Blob (legacy cache)
    else if (value) audio_blob[id] = new Blob([value], { type: 'audio/mpeg' });
  }
  return { ...row, audio_blob };
}

/* -------------------------------------------------------------------------- */
/* Phrases (own store, own audio)                                             */
/* -------------------------------------------------------------------------- */

/**
 * Flatten a topic detail's `levels[].phrases` maps into `phrases` rows.
 *
 * Each row keeps the phrase as the backend sent it (`content`, `translation`,
 * `description`, `audio`, `examples`) and adds the links the store itself needs:
 * which topic and level it belongs to, and where it sat in each — the payload
 * gives phrases no `position`, so their order in the map is the order we keep.
 *
 * @param {string} topicId Topic id.
 * @param {Array<object>} levels `levels` from `/study/topic/{id}`.
 */
function phraseRows(topicId, levels) {
  const rows = [];
  (Array.isArray(levels) ? levels : []).forEach((level, levelIndex) => {
    const phrases = level?.phrases;
    if (!phrases || typeof phrases !== 'object') return;
    Object.values(phrases).forEach((phrase, index) => {
      if (!phrase?.id) return;
      rows.push({
        ...phrase,
        topicId,
        levelId: level.id ?? null,
        levelName: level.name ?? null,
        levelPosition: Number.isFinite(level.position) ? level.position : levelIndex,
        position: index,
      });
    });
  });
  return rows;
}

/** The raw cached rows for one topic's phrases (audio still as ArrayBuffers). */
function getPhraseRows(topicId) {
  return getAllBy(STORES.phrases, 'topicId', topicId);
}

/**
 * Re-import a topic's phrases: drop every row it already had, then write the ones
 * in the payload with their audio downloaded as raw binary.
 *
 * A full replace rather than an upsert — this only ever runs right after the
 * detail payload was (re)fetched, i.e. on a cold cache or a content update, and
 * wiping first is what keeps a renamed, removed or re-recorded phrase from
 * lingering in the store.
 *
 * @param {string} topicId Topic id.
 * @param {Array<object>} levels `levels` from `/study/topic/{id}`.
 * @param {string} jwt Access token — the clip fetches are authenticated.
 */
async function syncPhrases(topicId, levels, jwt) {
  const stale = await getPhraseRows(topicId);
  await Promise.all(stale.map((row) => remove(STORES.phrases, row.id)));
  await Promise.all(
    phraseRows(topicId, levels).map(async (row) => {
      const audio_blob = await fetchAudioBinary(row.audio, jwt);
      await put(STORES.phrases, { ...row, audio_blob });
    })
  );
}

/** Rebuild a phrase row's stored binary into a playable `Blob`. */
function hydratePhraseAudio(row) {
  const value = row?.audio_blob;
  if (!value || value instanceof Blob) return row;
  return { ...row, audio_blob: new Blob([value], { type: 'audio/mpeg' }) };
}

/**
 * A single cached phrase by id, audio handed out as a playable `Blob`, or
 * `undefined` when it isn't cached.
 *
 * Purely local — what the phrase preview panel renders is whatever the topic's
 * detail fetch happened to store, never a fresh request.
 *
 * @param {string} phraseId Phrase id.
 */
export async function getPhrase(phraseId) {
  const row = await get(STORES.phrases, phraseId);
  return row ? hydratePhraseAudio(row) : undefined;
}

/**
 * A topic's phrase clips as `phraseId → Blob`, for the topic screen's
 * per-phrase play buttons.
 *
 * Fills the store on the fly when the topic's body is cached but its phrases
 * aren't: a topic cached before phrases had a store of their own is never
 * refetched on its own (its `version` hasn't moved), so the import is done here,
 * once, from the payload already in hand.
 *
 * Best-effort throughout: an unreadable store or a failed import yields an empty
 * map, which just renders the phrases with their play button disabled.
 *
 * @param {string} jwt Access token.
 * @param {string} topicId Topic id.
 * @param {object} topic The cached detail row.
 */
async function getPhraseAudio(jwt, topicId, topic) {
  const audio = {};
  try {
    let rows = await getPhraseRows(topicId);
    if (!rows.length && phraseRows(topicId, topic?.levels).length) {
      await syncPhrases(topicId, topic.levels, jwt);
      rows = await getPhraseRows(topicId);
    }
    for (const row of rows.map(hydratePhraseAudio)) {
      if (row.audio_blob instanceof Blob) audio[row.id] = row.audio_blob;
    }
  } catch {
    /* store unavailable / import failed — no phrase audio */
  }
  return audio;
}

/* -------------------------------------------------------------------------- */

/**
 * Fetch a topic's full content, download every audio clip once as raw binary,
 * and persist the whole payload (plus the `audio_blob` binary map) into the
 * `topics` store. Returns the stored row (audio still as ArrayBuffers).
 *
 * The payload's `audio` map covers dialogue sentences only, so the levels'
 * phrases are re-imported into their own store here, each with its own clip.
 */
async function fetchAndStoreTopic(jwt, topicId) {
  const remote = await fetchStudyTopic(jwt, topicId);
  const audio_blob = await fetchAudioBinaries(remote?.audio, jwt);
  const row = { ...remote, id: remote?.id ?? topicId, audio_blob };
  await put(STORES.topics, row);
  await syncPhrases(row.id, remote?.levels, jwt);
  return row;
}

/**
 * Resolve the full content for a single topic, fetching from the network only
 * when needed:
 *   - head flagged `needUpdate`  → drop the cached row, refetch, then clear the
 *                                  head's flag (mirrors the chapter→topics flow)
 *   - not cached                 → fetch, download audio, store, then read back
 *   - cached & fresh             → return the cached row, no request
 *
 * The `topics_head` row (title / intro / thumbnail) is returned alongside the
 * detail so the header can be rendered — the detail payload itself carries no
 * title or intro — and so is the `phraseId → Blob` map of the phrase clips,
 * which live in their own store rather than in the topic's `audio` map.
 *
 * @param {string} jwt Access token.
 * @param {string} topicId Topic id.
 * @returns {Promise<{head: ?object, topic: object, phraseAudio: Record<string, Blob>}>}
 */
export async function loadTopic(jwt, topicId) {
  const head = await getTopicHead(topicId);

  // Head says the content moved on → discard the cached body so it re-downloads.
  if (head?.needUpdate) {
    await remove(STORES.topics, topicId);
  }

  let resolvedHead = head;
  let topic = head?.needUpdate ? undefined : await get(STORES.topics, topicId);

  if (!topic) {
    topic = await fetchAndStoreTopic(jwt, topicId);
    // Refresh succeeded → clear the head's stale flag so we don't refetch forever.
    if (head?.needUpdate) {
      resolvedHead = { ...head, needUpdate: false };
      await put(STORES.topicsHead, resolvedHead);
    }
  }

  // Hand out playable Blobs even though the cache holds raw binary.
  return {
    head: resolvedHead,
    topic: hydrateAudioBlob(topic),
    phraseAudio: await getPhraseAudio(jwt, topicId, topic),
  };
}
